import { useLocation, useRouteLoaderData } from '@remix-run/react';
import React, { useEffect, useRef, useState } from 'react';
import { Navbar as BsNavbar, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import MegaMenu from '../../components/layout/MegaMenu';

import DropCart from './header/DropCart';
import NotificationBanner from './header/NotificationBanner';
import UserNavigation from './header/UserNavigation';


import Contact from '~/components/layout/header/Contact';
import Search from '~/components/layout/header/Search';
import ShopLogo from '~/components/layout/header/ShopLogo';
import { useRoutePath } from '~/hooks/useRoutePath';
import type { loader as rootLoader } from '~/root';

type HeaderProps = {
  isCart?: boolean;
};

const Header = ({ isCart = false }: HeaderProps) => {
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const taxonTree = rootData?.taxonTree ?? [];
  const notificationContent = rootData?.notificationContent ?? null;

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const routePath = useRoutePath();

  // The logo doubles as the homepage <h1> — but only there, so every other page keeps its
  // own heading. Trailing slashes are normalised so `/en/` still matches `/en`.
  const homePath = routePath('homepage');
  const isHomepage = (pathname.replace(/\/+$/, '') || '/') === homePath;

  // The <h1> needs real text for SEO; the logo mark alone is not indexable copy.
  const seoHeading = t('pages.homepage.h1');

  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Expose the full sticky-header height (header bar + mega menu) so sticky page
  // elements can offset below it and stay clear when the menu re-appears.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    const setHeaderHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };

    setHeaderHeight();
    const observer = new ResizeObserver(setHeaderHeight);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isCart) {
      return undefined;
    }

    const SCROLL_THRESHOLD = 80;
    const SCROLL_DEADZONE = 4;

    const update = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      if (Math.abs(delta) >= SCROLL_DEADZONE) {
        if (delta > 0 && currentScrollY > SCROLL_THRESHOLD) {
          setHidden(true);
        } else if (delta < 0) {
          setHidden(false);
        }
        lastScrollY.current = currentScrollY;
      }

      if (currentScrollY <= SCROLL_THRESHOLD) {
        setHidden(false);
      }

      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(update);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isCart]);

  const handleToggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };
  const headerClassName = isCart ? 'header header--visible' : `header ${hidden ? 'header--hidden' : 'header--visible'}`;

  return (
    <header className={headerClassName} ref={headerRef}>
      {!isCart && <NotificationBanner content={notificationContent} />}
      <div className="header-main">
        <Container className="d-flex justify-content-sm-between py-3 align-items-center gap-3 gap-sm-4 flex-sm-wrap flex-wrap">
          {isHomepage ? (
            <h1 className="header-logo-heading d-inline-flex align-items-center m-0 me-auto">
              <ShopLogo
                size={{ width: 222, height: 46 }}
                className="d-inline-block py-lg-2 header-logo"
                srText={seoHeading}
              />
            </h1>
          ) : (
            <ShopLogo size={{ width: 222, height: 46 }} className="d-inline-block py-lg-2 me-auto header-logo" />
          )}
          {!isCart && <Search />}
          <Contact />
          <UserNavigation showLabel={false} />
          {!isCart && (
            <>
              <DropCart />
              <BsNavbar.Toggle
                className="btn-icon btn-transparent px-0 d-lg-none navbar navbar-light"
                onClick={handleToggleMenu}
              />
            </>
          )}
        </Container>
      </div>
      {!isCart && (
        <div className="megamenu-collapse">
          <MegaMenu taxonTree={taxonTree} show={menuOpen} onHide={handleToggleMenu} />
        </div>
      )}
    </header>
  );
};

export default Header;
